import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GPUComputationRenderer } from 'three/addons/misc/GPUComputationRenderer.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js'
import { Pane } from 'tweakpane'
import * as EssentialsPlugin from '@tweakpane/plugin-essentials'
import particlesVertexShader from './shaders/particles/vertex.glsl'
import particlesFragmentShader from './shaders/particles/fragment.glsl'
import gpgpuParticlesShader from './shaders/gpgpu/particles.glsl'
import gpgpuVelocityShader from './shaders/gpgpu/velocity.glsl'

/**
 * Base
 */
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

/**
 * Params
 */
const params = {
    // Aspetto
    particleSize: 0.07,
    particleColor: '#cccccc',
    colorShift: '#6688cc',
    backgroundColor: '#080808',
    opacityVariance: 0.4,
    colorVariance: 0.5,
    // Campo di flusso
    flowInfluence: 0.5,
    flowStrength: 2,
    flowFrequency: 0.5,
    // Mouse
    mouseRadius: 5,
    mouseAspect: 3.0,
    suckStrength: 14,
    windStrength: 3.7,
    mouseTrail: 0,
    // Fisica
    damping: 2.2,
    turbulenceStrength: 3.0,
    turbulenceFrequency: 1.8,
    // Camera
    autoRotate: true,
    autoRotateSpeed: 3.7,
    // Bloom
    bloomEnabled: true,
    bloomStrength: 0.8,
    bloomRadius: 0.4,
    bloomThreshold: 0.2,
}

const presets = {
    'Fumo classico': { particleColor: '#cccccc', colorShift: '#6688cc', backgroundColor: '#080808' },
    'Fuoco': { particleColor: '#ff4400', colorShift: '#ffcc00', backgroundColor: '#1a0500' },
    'Nebulosa': { particleColor: '#aa44ff', colorShift: '#0088ff', backgroundColor: '#04050f' },
}

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () =>
{
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)

    composer.setSize(sizes.width, sizes.height)
    composer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 18)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.enableZoom = false
controls.enablePan = false
controls.enableRotate = false
controls.autoRotate = params.autoRotate
controls.autoRotateSpeed = 3.7

/**
 * Mouse interaction
 */
const mouse = new THREE.Vector2(9999, 9999)
const mouseWorld = new THREE.Vector3(9999, 9999, 9999)
const raycaster = new THREE.Raycaster()
const mousePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
const mouseIntersectPoint = new THREE.Vector3()

window.addEventListener('pointermove', (event) =>
{
    mouse.x = (event.clientX / sizes.width) * 2 - 1
    mouse.y = -(event.clientY / sizes.height) * 2 + 1
})

const previousMouseWorld = new THREE.Vector3(9999, 9999, 9999)
const mouseVelocity = new THREE.Vector3()
const smoothedMouseVelocity = new THREE.Vector3()

let isMouseDown = false
let suckAmount = 0

window.addEventListener('pointerdown', () => { isMouseDown = true })
window.addEventListener('pointerup', () => { isMouseDown = false })
window.addEventListener('pointerleave', () =>
{
    isMouseDown = false
    mouseWorld.set(9999, 9999, 9999)
})

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)
renderer.setClearColor(params.backgroundColor)

/**
 * Post-processing
 */
const composer = new EffectComposer(renderer)
const renderPass = new RenderPass(scene, camera)
composer.addPass(renderPass)

const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(sizes.width, sizes.height),
    params.bloomStrength,
    params.bloomRadius,
    params.bloomThreshold
)
composer.addPass(bloomPass)

/**
 * Load font and create text geometry
 */
const fontLoader = new FontLoader()
const font = await fontLoader.loadAsync('https://threejs.org/examples/fonts/helvetiker_bold.typeface.json')

const textGeometry = new TextGeometry('SMOKE', {
    font: font,
    size: 2,
    depth: 0.4,
    curveSegments: 12,
    bevelEnabled: true,
    bevelThickness: 0.05,
    bevelSize: 0.05,
    bevelSegments: 3
})
textGeometry.center()

/**
 * Sample points on text surface
 */
const textMesh = new THREE.Mesh(textGeometry, new THREE.MeshBasicMaterial())
const sampler = new MeshSurfaceSampler(textMesh).build()
const particleCount = 20000
const sampledPositions = new Float32Array(particleCount * 3)
const tempPosition = new THREE.Vector3()

for(let i = 0; i < particleCount; i++)
{
    sampler.sample(tempPosition)
    sampledPositions[i * 3 + 0] = tempPosition.x
    sampledPositions[i * 3 + 1] = tempPosition.y
    sampledPositions[i * 3 + 2] = tempPosition.z
}

/**
 * Base geometry
 */
const baseGeometry = {}
baseGeometry.instance = new THREE.BufferGeometry()
baseGeometry.instance.setAttribute('position', new THREE.BufferAttribute(sampledPositions, 3))
baseGeometry.count = particleCount

/**
 * GPU Compute
 */
const gpgpu = {}
gpgpu.size = Math.ceil(Math.sqrt(baseGeometry.count))
gpgpu.computation = new GPUComputationRenderer(gpgpu.size, gpgpu.size, renderer)

const baseParticlesTexture = gpgpu.computation.createTexture()

for(let i = 0; i < baseGeometry.count; i++)
{
    const i3 = i * 3
    const i4 = i * 4
    baseParticlesTexture.image.data[i4 + 0] = sampledPositions[i3 + 0]
    baseParticlesTexture.image.data[i4 + 1] = sampledPositions[i3 + 1]
    baseParticlesTexture.image.data[i4 + 2] = sampledPositions[i3 + 2]
    baseParticlesTexture.image.data[i4 + 3] = Math.random()
}

const baseVelocityTexture = gpgpu.computation.createTexture()

gpgpu.particlesVariable = gpgpu.computation.addVariable('uParticles', gpgpuParticlesShader, baseParticlesTexture)
gpgpu.velocityVariable = gpgpu.computation.addVariable('uVelocity', gpgpuVelocityShader, baseVelocityTexture)

gpgpu.computation.setVariableDependencies(gpgpu.particlesVariable, [ gpgpu.particlesVariable, gpgpu.velocityVariable ])
gpgpu.computation.setVariableDependencies(gpgpu.velocityVariable, [ gpgpu.velocityVariable, gpgpu.particlesVariable ])

// Particles uniforms
gpgpu.particlesVariable.material.uniforms.uTime = new THREE.Uniform(0)
gpgpu.particlesVariable.material.uniforms.uDeltaTime = new THREE.Uniform(0)
gpgpu.particlesVariable.material.uniforms.uBase = new THREE.Uniform(baseParticlesTexture)
gpgpu.particlesVariable.material.uniforms.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
gpgpu.particlesVariable.material.uniforms.uMouseRadius = new THREE.Uniform(params.mouseRadius)
gpgpu.particlesVariable.material.uniforms.uMouseAspect = new THREE.Uniform(params.mouseAspect)
gpgpu.particlesVariable.material.uniforms.uSuckAmount = new THREE.Uniform(0)
gpgpu.particlesVariable.material.uniforms.uMouseTrail = new THREE.Uniform(params.mouseTrail)

// Velocity uniforms
gpgpu.velocityVariable.material.uniforms.uTime = new THREE.Uniform(0)
gpgpu.velocityVariable.material.uniforms.uDeltaTime = new THREE.Uniform(0)
gpgpu.velocityVariable.material.uniforms.uBase = new THREE.Uniform(baseParticlesTexture)
gpgpu.velocityVariable.material.uniforms.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
gpgpu.velocityVariable.material.uniforms.uMouseRadius = new THREE.Uniform(params.mouseRadius)
gpgpu.velocityVariable.material.uniforms.uMouseAspect = new THREE.Uniform(params.mouseAspect)
gpgpu.velocityVariable.material.uniforms.uSuckAmount = new THREE.Uniform(0)
gpgpu.velocityVariable.material.uniforms.uSuckStrength = new THREE.Uniform(params.suckStrength)
gpgpu.velocityVariable.material.uniforms.uMouseVelocity = new THREE.Uniform(new THREE.Vector3())
gpgpu.velocityVariable.material.uniforms.uWindStrength = new THREE.Uniform(params.windStrength)
gpgpu.velocityVariable.material.uniforms.uDamping = new THREE.Uniform(params.damping)
gpgpu.velocityVariable.material.uniforms.uTurbulenceStrength = new THREE.Uniform(params.turbulenceStrength)
gpgpu.velocityVariable.material.uniforms.uTurbulenceFrequency = new THREE.Uniform(params.turbulenceFrequency)
gpgpu.velocityVariable.material.uniforms.uFlowFieldFrequency = new THREE.Uniform(params.flowFrequency)
gpgpu.velocityVariable.material.uniforms.uFlowFieldStrength = new THREE.Uniform(params.flowStrength)
gpgpu.velocityVariable.material.uniforms.uFlowFieldInfluence = new THREE.Uniform(params.flowInfluence)

gpgpu.computation.init()

/**
 * Particles
 */
const particles = {}

const particlesUvArray = new Float32Array(baseGeometry.count * 2)
const sizesArray = new Float32Array(baseGeometry.count)

for(let y = 0; y < gpgpu.size; y++)
{
    for(let x = 0; x < gpgpu.size; x++)
    {
        const i = (y * gpgpu.size + x)
        const i2 = i * 2
        particlesUvArray[i2 + 0] = (x + 0.5) / gpgpu.size
        particlesUvArray[i2 + 1] = (y + 0.5) / gpgpu.size
        sizesArray[i] = Math.random()
    }
}

particles.geometry = new THREE.BufferGeometry()
particles.geometry.setDrawRange(0, baseGeometry.count)
particles.geometry.setAttribute('aParticlesUv', new THREE.BufferAttribute(particlesUvArray, 2))
particles.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizesArray, 1))

const colorFromHex = (hex) =>
{
    const c = new THREE.Color(hex)
    return new THREE.Vector3(c.r, c.g, c.b)
}

particles.material = new THREE.ShaderMaterial({
    vertexShader: particlesVertexShader,
    fragmentShader: particlesFragmentShader,
    transparent: true,
    depthWrite: false,
    uniforms:
    {
        uSize: new THREE.Uniform(params.particleSize),
        uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
        uParticlesTexture: new THREE.Uniform(),
        uColor: new THREE.Uniform(colorFromHex(params.particleColor)),
        uColorShift: new THREE.Uniform(colorFromHex(params.colorShift)),
        uTime: new THREE.Uniform(0),
        uOpacityVariance: new THREE.Uniform(params.opacityVariance),
        uColorVariance: new THREE.Uniform(params.colorVariance)
    }
})

particles.points = new THREE.Points(particles.geometry, particles.material)
scene.add(particles.points)

/**
 * Tweakpane
 */
const pane = new Pane({ title: 'Controlli' })
pane.registerPlugin(EssentialsPlugin)

// --- Preset colori ---
const presetFolder = pane.addFolder({ title: 'Preset colori' })
for(const [name, preset] of Object.entries(presets))
{
    presetFolder.addButton({ title: name }).on('click', () =>
    {
        params.particleColor = preset.particleColor
        params.colorShift = preset.colorShift
        params.backgroundColor = preset.backgroundColor
        renderer.setClearColor(params.backgroundColor)
        particles.material.uniforms.uColor.value = colorFromHex(params.particleColor)
        particles.material.uniforms.uColorShift.value = colorFromHex(params.colorShift)
        pane.refresh()
    })
}

// --- Aspetto ---
const aspectFolder = pane.addFolder({ title: 'Aspetto' })
aspectFolder.addBinding(params, 'particleSize', { label: 'Dimensione', min: 0, max: 1, step: 0.001 })
    .on('change', (e) => { particles.material.uniforms.uSize.value = e.value })
aspectFolder.addBinding(params, 'particleColor', { label: 'Colore particelle', view: 'color' })
    .on('change', (e) => { particles.material.uniforms.uColor.value = colorFromHex(e.value) })
aspectFolder.addBinding(params, 'colorShift', { label: 'Colore secondario', view: 'color' })
    .on('change', (e) => { particles.material.uniforms.uColorShift.value = colorFromHex(e.value) })
aspectFolder.addBinding(params, 'colorVariance', { label: 'Varianza colore', min: 0, max: 1, step: 0.01 })
    .on('change', (e) => { particles.material.uniforms.uColorVariance.value = e.value })
aspectFolder.addBinding(params, 'opacityVariance', { label: 'Varianza opacit\u00e0', min: 0, max: 1, step: 0.01 })
    .on('change', (e) => { particles.material.uniforms.uOpacityVariance.value = e.value })
aspectFolder.addBinding(params, 'backgroundColor', { label: 'Colore sfondo', view: 'color' })
    .on('change', (e) => { renderer.setClearColor(e.value) })

// --- Campo di flusso ---
const flowFolder = pane.addFolder({ title: 'Campo di flusso' })
flowFolder.addBinding(params, 'flowInfluence', { label: 'Influenza', min: 0, max: 1, step: 0.001 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uFlowFieldInfluence.value = e.value })
flowFolder.addBinding(params, 'flowStrength', { label: 'Intensit\u00e0', min: 0, max: 10, step: 0.001 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uFlowFieldStrength.value = e.value })
flowFolder.addBinding(params, 'flowFrequency', { label: 'Frequenza', min: 0, max: 1, step: 0.001 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uFlowFieldFrequency.value = e.value })

// --- Mouse ---
const mouseFolder = pane.addFolder({ title: 'Interazione mouse' })
mouseFolder.addBinding(params, 'mouseRadius', { label: 'Raggio', min: 0, max: 10, step: 0.01 })
    .on('change', (e) => {
        gpgpu.particlesVariable.material.uniforms.uMouseRadius.value = e.value
        gpgpu.velocityVariable.material.uniforms.uMouseRadius.value = e.value
    })
mouseFolder.addBinding(params, 'mouseAspect', { label: 'Larghezza barra', min: 1, max: 10, step: 0.1 })
    .on('change', (e) => {
        gpgpu.particlesVariable.material.uniforms.uMouseAspect.value = e.value
        gpgpu.velocityVariable.material.uniforms.uMouseAspect.value = e.value
    })
mouseFolder.addBinding(params, 'suckStrength', { label: 'Forza risucchio', min: 0, max: 30, step: 0.1 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uSuckStrength.value = e.value })
mouseFolder.addBinding(params, 'windStrength', { label: 'Forza vento', min: 0, max: 20, step: 0.1 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uWindStrength.value = e.value })
mouseFolder.addBinding(params, 'mouseTrail', { label: 'Scia', min: 0, max: 1, step: 0.01 })
    .on('change', (e) => { gpgpu.particlesVariable.material.uniforms.uMouseTrail.value = e.value })

// --- Fisica ---
const physicsFolder = pane.addFolder({ title: 'Fisica' })
physicsFolder.addBinding(params, 'damping', { label: 'Attrito (meno = pi\u00f9 inerzia)', min: 0, max: 10, step: 0.1 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uDamping.value = e.value })
physicsFolder.addBinding(params, 'turbulenceStrength', { label: 'Turbolenza', min: 0, max: 10, step: 0.1 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uTurbulenceStrength.value = e.value })
physicsFolder.addBinding(params, 'turbulenceFrequency', { label: 'Frequenza turbolenza', min: 0, max: 10, step: 0.1 })
    .on('change', (e) => { gpgpu.velocityVariable.material.uniforms.uTurbulenceFrequency.value = e.value })

// --- Camera ---
const cameraFolder = pane.addFolder({ title: 'Camera' })
cameraFolder.addBinding(params, 'autoRotate', { label: 'Rotazione automatica' })
    .on('change', (e) => { controls.autoRotate = e.value })
cameraFolder.addBinding(params, 'autoRotateSpeed', { label: 'Velocit\u00e0 rotazione', min: 0, max: 10, step: 0.1 })
    .on('change', (e) => { controls.autoRotateSpeed = e.value })

// --- Bloom ---
const bloomFolder = pane.addFolder({ title: 'Bloom' })
bloomFolder.addBinding(params, 'bloomEnabled', { label: 'Attivo' })
bloomFolder.addBinding(params, 'bloomStrength', { label: 'Intensit\u00e0', min: 0, max: 3, step: 0.01 })
    .on('change', (e) => { bloomPass.strength = e.value })
bloomFolder.addBinding(params, 'bloomRadius', { label: 'Raggio', min: 0, max: 2, step: 0.01 })
    .on('change', (e) => { bloomPass.radius = e.value })
bloomFolder.addBinding(params, 'bloomThreshold', { label: 'Soglia', min: 0, max: 1, step: 0.01 })
    .on('change', (e) => { bloomPass.threshold = e.value })

// --- FPS ---
const fpsGraph = pane.addBlade({ view: 'fpsgraph', label: 'FPS', rows: 2 })

/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0

const tick = () =>
{
    fpsGraph.begin()

    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    controls.update()

    // Mouse world position
    raycaster.setFromCamera(mouse, camera)
    mousePlane.normal.copy(camera.getWorldDirection(new THREE.Vector3()))
    if(raycaster.ray.intersectPlane(mousePlane, mouseIntersectPoint))
    {
        mouseWorld.copy(mouseIntersectPoint)
    }

    // Mouse velocity
    if(previousMouseWorld.x < 9000.0 && mouseWorld.x < 9000.0 && deltaTime > 0)
    {
        mouseVelocity.subVectors(mouseWorld, previousMouseWorld).divideScalar(deltaTime)
    }
    else
    {
        mouseVelocity.set(0, 0, 0)
    }
    smoothedMouseVelocity.lerp(mouseVelocity, Math.min(deltaTime * 8.0, 1.0))
    previousMouseWorld.copy(mouseWorld)

    // Suck transition
    const suckTarget = isMouseDown ? 1.0 : 0.0
    suckAmount += (suckTarget - suckAmount) * deltaTime * (isMouseDown ? 5.0 : 2.0)

    // GPGPU uniforms
    const pU = gpgpu.particlesVariable.material.uniforms
    const vU = gpgpu.velocityVariable.material.uniforms

    pU.uTime.value = elapsedTime
    pU.uDeltaTime.value = deltaTime
    pU.uMouse.value.copy(mouseWorld)
    pU.uSuckAmount.value = suckAmount

    vU.uTime.value = elapsedTime
    vU.uDeltaTime.value = deltaTime
    vU.uMouse.value.copy(mouseWorld)
    vU.uSuckAmount.value = suckAmount
    vU.uMouseVelocity.value.copy(smoothedMouseVelocity)

    gpgpu.computation.compute()
    particles.material.uniforms.uParticlesTexture.value = gpgpu.computation.getCurrentRenderTarget(gpgpu.particlesVariable).texture
    particles.material.uniforms.uTime.value = elapsedTime

    if(params.bloomEnabled)
    {
        composer.render()
    }
    else
    {
        renderer.render(scene, camera)
    }

    fpsGraph.end()
    window.requestAnimationFrame(tick)
}

tick()
